// QuestionMediaDisplay.jsx - Display media in taker/preview views
// Place in: src/components/leitner/QuestionMediaDisplay.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Maximize2, Minimize2, X, Play, Pause,
  ZoomIn, ZoomOut, RotateCw, ExternalLink
} from 'lucide-react';

const QuestionMediaDisplay = ({ media, className = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [zoom, setZoom] = useState(1);

  const iframeRef = React.useRef(null);


  // Auto-slideshow
  useEffect(() => {
    if (!isPlaying || media?.type !== 'images' || media.images?.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % media.images.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPlaying, media]);

  // Keyboard navigation
  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
      if (e.key === 'ArrowLeft') navigateImage(-1);
      if (e.key === 'ArrowRight') navigateImage(1);
      if (e.key === ' ') {
        e.preventDefault();
        setIsPlaying(p => !p);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, media]);

  const navigateImage = useCallback((direction) => {
    if (media?.type !== 'images' || !media.images?.length) return;
    setCurrentIndex(prev => {
      const newIndex = prev + direction;
      if (newIndex < 0) return media.images.length - 1;
      if (newIndex >= media.images.length) return 0;
      return newIndex;
    });
  }, [media]);

  const getImageSrc = (img) => {
    if (!img) return '';
    if (typeof img === 'string') return img;
    if (img instanceof File) return URL.createObjectURL(img);
    return img.url || '';
  };

  const getYoutubeEmbedUrl = (videoId) => {
    return `https://www.youtube.com/embed/${videoId}?rel=0`;
  };

  if (!media || media.type === 'none') return null;

  // Fullscreen Modal
  const FullscreenModal = () => (
    <div
      className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
      onClick={() => setIsFullscreen(false)}
    >
      {/* Controls */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        {media.type === 'images' && media.images?.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setIsPlaying(p => !p); }}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition"
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>
          </>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(z + 0.5, 3)); }}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition"
        >
          <ZoomIn size={20} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(z - 0.5, 0.5)); }}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition"
        >
          <ZoomOut size={20} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setZoom(1); }}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition"
        >
          <RotateCw size={20} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); setIsFullscreen(false); }}
          className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition"
        >
          <X size={20} />
        </button>
      </div>

      {/* Counter */}
      {media.type === 'images' && media.images?.length > 1 && (
        <div className="absolute top-4 left-4 px-4 py-2 bg-black/50 rounded-full text-white text-sm">
          {currentIndex + 1} / {media.images.length}
          {isPlaying && <span className="ml-2 text-green-400">● Auto</span>}
        </div>
      )}

      {/* Content */}
      <div
        className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {media.type === 'images' && (
          <>
            <img
              src={getImageSrc(media.images[currentIndex])}
              alt={`Image ${currentIndex + 1}`}
              className="w-[600px] h-auto object-contain transition-transform duration-200"
              style={{ transform: `scale(${zoom})` }}
            />

            {/* Navigation */}
            {media.images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); navigateImage(-1); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition"
                >
                  <ChevronLeft size={32} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); navigateImage(1); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition"
                >
                  <ChevronRight size={32} />
                </button>
              </>
            )}

            {/* Dots */}
            {media.images.length > 1 && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                {media.images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx); }}
                    className={`w-3 h-3 rounded-full transition ${idx === currentIndex ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                      }`}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {media.type === 'video' && (
          <div className="w-[80vw] max-w-4xl aspect-video">
            {media.video?.source === 'youtube' ? (
              <iframe
                src={getYoutubeEmbedUrl(media.video.videoId)}
                className="w-full h-full rounded-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video"
              />
            ) : (
              <video
                src={media.video?.url}
                controls
                autoPlay
                className="w-full h-full rounded-lg"
              />
            )}
          </div>
        )}

        {media.type === 'embed' && (
          <div className="
            relative group
            w-[90vw]
            sm:w-[640px]
            md:w-[768px]
            lg:w-[900px]
            aspect-video
          ">
            {media.embed?.embedType === 'html' ? (
              <iframe
                srcDoc={`<!DOCTYPE html><html><head><style>body{margin:0;background:#000;color:white;font-family:sans-serif;}</style></head><body>${media.embed.html}</body></html>`}
                className="w-full h-full rounded-lg bg-white"
                sandbox="allow-scripts allow-same-origin allow-popups"
                title="Embedded content"
              />
            ) : (
              <div className="w-full h-full relative">
                <iframe
                  src={media.embed?.url}
                  className="w-full h-full rounded-lg bg-white"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                  title="Embedded URL"
                />
                {/* Fallback Open Link Button - always visible in fullscreen for URLs */}
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/40 opacity-0 hover:opacity-100 transition-opacity">
                  <a
                    href={media.embed?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-2xl transition-transform hover:scale-105 pointer-events-auto"
                  >
                    <ExternalLink size={20} />
                    Open in New Tab
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Regular Display
  return (
    <>
      <div className={`relative ${className}`}>
        {/* Images Slideshow */}
        {media.type === 'images' && media.images?.length > 0 && (
          <div className="relative bg-slate-800/50 rounded-lg overflow-hidden">
            <div className="aspect-video flex items-center justify-center bg-slate-900/50">
              <img
                src={getImageSrc(media.images[currentIndex])}
                alt={`Image ${currentIndex + 1}`}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            {/* Fullscreen Button */}
            <button
              onClick={() => setIsFullscreen(true)}
              className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg text-white transition"
            >
              <Maximize2 size={18} />
            </button>

            {/* Navigation */}
            {media.images.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage(-1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => navigateImage(1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition"
                >
                  <ChevronRight size={20} />
                </button>

                {/* Counter & Autoplay */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2">
                  <button
                    onClick={() => setIsPlaying(p => !p)}
                    className="p-1.5 bg-black/50 hover:bg-black/70 rounded-full text-white transition"
                  >
                    {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                  </button>
                  <span className="px-3 py-1 bg-black/60 rounded-full text-white text-sm">
                    {currentIndex + 1} / {media.images.length}
                  </span>
                </div>

                {/* Dots */}
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {media.images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2 h-2 rounded-full transition ${idx === currentIndex ? 'bg-white' : 'bg-white/40 hover:bg-white/60'
                        }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Video */}
        {media.type === 'video' && media.video && (
          <div className="relative bg-slate-800/50 rounded-lg overflow-hidden">
            {media.video.source === 'youtube' ? (
              <div className="aspect-video">
                <iframe
                  src={getYoutubeEmbedUrl(media.video.videoId)}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Video"
                />
              </div>
            ) : (
              <video
                src={media.video.url}
                controls
                className="w-full aspect-video object-contain bg-black"
              />
            )}

            {/* Fullscreen Button */}
            <button
              onClick={() => setIsFullscreen(true)}
              className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg text-white transition z-10"
            >
              <Maximize2 size={18} />
            </button>
          </div>
        )}

        {/* Embed */}
        {media.type === 'embed' && (media.embed?.url || media.embed?.html) && (
          <div
            className={`
              relative group transition-all overflow-hidden rounded-lg bg-slate-800
              ${isFullscreen
                ? 'w-[95vw] h-[95vh]'
                : 'w-full max-w-[800px] aspect-video'
              }
            `}
          >
            {media.embed.embedType === 'html' ? (
              <iframe
                ref={iframeRef}
                srcDoc={`<!DOCTYPE html>
                <html>
                <head>
                  <style>
                    html, body {
                      width: 100%;
                      height: 100%;
                      margin: 0;
                      background: #1e293b;
                      color: white;
                      font-family: sans-serif;
                    }
                    body {
                      display: block;
                      overflow: auto;
                    }
                  </style>
                </head>
                <body>
                  ${media.embed.html}
                </body>
                </html>`}
                className="w-full h-full border-none bg-white"
                sandbox="allow-scripts allow-same-origin allow-popups"
                title="Embedded content"
              />
            ) : (
              <>
                <iframe
                  src={media.embed?.url}
                  className="w-full h-full border-none bg-white"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                  title="Embedded URL"
                />
                {/* Fallback Open Link Button */}
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="pointer-events-auto">
                    <a
                      href={media.embed?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium shadow-lg transition-transform hover:scale-105"
                    >
                      <ExternalLink size={16} />
                      Open Link
                    </a>
                  </div>
                </div>
              </>
            )}

            {/* Fullscreen Toggle for Embed if not in fullscreen */}
            {!isFullscreen && (
              <button
                onClick={() => setIsFullscreen(true)}
                className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-lg text-white transition opacity-0 group-hover:opacity-100"
              >
                <Maximize2 size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && <FullscreenModal />}
    </>
  );
};

export default QuestionMediaDisplay;